import { ShipTier, ShipTierColors } from "@voidex/contracts-ts";

interface ShipIconProps {
	tier: ShipTier;
	size?: "sm" | "md" | "lg" | "xl";
}

const SIZE_PX: Record<NonNullable<ShipIconProps["size"]>, number> = {
	sm: 24,
	md: 40,
	lg: 64,
	xl: 120,
};

function ProbeShip({ color }: { color: string }) {
	return (
		<svg viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<title>Probe</title>
			{/* Sleek thin dart body */}
			<path
				d="M6 12L20 8L38 12L20 16Z"
				fill={`${color}22`}
				stroke={color}
				strokeWidth="0.8"
				strokeOpacity="0.6"
			/>
			{/* Sensor dome at nose */}
			<circle cx="33" cy="12" r="2.5" fill={`${color}33`} />
			{/* Single engine exhaust */}
			<ellipse cx="6" cy="12" rx="3" ry="2" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.5s" repeatCount="indefinite" />
				<animate attributeName="opacity" values="0.5;0.9;0.5" dur="0.5s" repeatCount="indefinite" />
			</ellipse>
		</svg>
	);
}

function FrigateShip({ color }: { color: string }) {
	return (
		<svg viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<title>Frigate</title>
			{/* Wide hexagonal hull — flat top & bottom */}
			<path
				d="M4 12L10 7L32 7L38 12L32 17L10 17Z"
				fill={`${color}22`}
				stroke={color}
				strokeWidth="0.8"
				strokeOpacity="0.6"
			/>
			{/* Top swept wing */}
			<path
				d="M14 7L10 1L26 3L22 7Z"
				fill={`${color}33`}
				stroke={color}
				strokeWidth="0.5"
				strokeOpacity="0.3"
			/>
			{/* Bottom swept wing */}
			<path
				d="M14 17L10 23L26 21L22 17Z"
				fill={`${color}33`}
				stroke={color}
				strokeWidth="0.5"
				strokeOpacity="0.3"
			/>
			{/* Command bridge glow */}
			<ellipse cx="30" cy="12" rx="5" ry="2.5" fill={`${color}44`} />
			{/* Dual engine exhausts */}
			<ellipse cx="5" cy="9" rx="3" ry="1.5" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.4s" repeatCount="indefinite" />
				<animate attributeName="opacity" values="0.5;0.9;0.5" dur="0.4s" repeatCount="indefinite" />
			</ellipse>
			<ellipse cx="5" cy="15" rx="3" ry="1.5" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.4s" repeatCount="indefinite" />
				<animate attributeName="opacity" values="0.5;0.9;0.5" dur="0.4s" repeatCount="indefinite" />
			</ellipse>
		</svg>
	);
}

function DreadnoughtShip({ color }: { color: string }) {
	return (
		<svg viewBox="0 0 40 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<title>Dreadnought</title>
			<path
				d="M2 12L10 1L38 12L10 23Z"
				fill={`${color}22`}
				stroke={color}
				strokeWidth="1"
				strokeOpacity="0.7"
			/>
			<path d="M12 1L20 6L12 6Z" fill={`${color}33`} />
			<path d="M12 23L20 18L12 18Z" fill={`${color}33`} />
			<path d="M18 6L26 10L18 10Z" fill={`${color}22`} />
			<path d="M18 18L26 14L18 14Z" fill={`${color}22`} />
			<ellipse cx="30" cy="12" rx="6" ry="3" fill={`${color}44`} />
			<ellipse cx="3" cy="7" rx="3" ry="1.5" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.35s" repeatCount="indefinite" />
			</ellipse>
			<ellipse cx="3" cy="12" rx="3" ry="1.5" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.3s" repeatCount="indefinite" />
			</ellipse>
			<ellipse cx="3" cy="17" rx="3" ry="1.5" fill={`${color}88`}>
				<animate attributeName="rx" values="2;4;2" dur="0.35s" repeatCount="indefinite" />
			</ellipse>
		</svg>
	);
}

const TIER_SHIPS: Record<ShipTier, React.ComponentType<{ color: string }>> = {
	[ShipTier.Probe]: ProbeShip,
	[ShipTier.Frigate]: FrigateShip,
	[ShipTier.Dreadnought]: DreadnoughtShip,
};

export function ShipIcon({ tier, size = "md" }: ShipIconProps) {
	const px = SIZE_PX[size];
	const color = ShipTierColors[tier];
	const ShipComponent = TIER_SHIPS[tier];

	return (
		<span
			className={`ship-icon ship-icon--${size}`}
			style={{ display: "inline-flex", width: px, height: px * 0.6 }}
			role="img"
			aria-label={`${ShipTier[tier]} ship`}
		>
			<ShipComponent color={color} />
		</span>
	);
}
